/**
 * InviteModal — invite a colleague to the admin portal (adds to allowlist +
 * sends a branded email).
 */
import React from "react";
import { Send } from "lucide-react";

export default function InviteModal({
  open, onClose, inviting,
  inviteEmail, setInviteEmail,
  inviteName, setInviteName,
  inviteNote, setInviteNote,
  inviteRole, setInviteRole,
  inviteTrialDays, setInviteTrialDays,
  onSubmit,
}) {
  if (!open) return null;
  const ROLES = [
    { value: "sales_manager", label: "Sales manager — full tools & pipeline" },
    { value: "sales_person", label: "Salesperson — tools, own pipeline" },
    { value: "trial_user", label: "Trial / prospect — time-limited evaluation" },
    { value: "campaign_user", label: "Campaign / one-off — single use" },
  ];
  const showTrialDays = inviteRole === "trial_user" || inviteRole === "campaign_user";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => !inviting && onClose()}
      data-testid="invite-modal-backdrop"
    >
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white max-w-lg w-full border border-zinc-200 shadow-xl"
        data-testid="invite-modal"
      >
        <div className="bg-black text-white p-6">
          <div className="ek-eyebrow text-[#A179FA]">Invite</div>
          <h3 className="ek-heading text-xl mt-1">Invite a colleague to the admin portal</h3>
          <p className="text-zinc-400 text-[13px] mt-2">
            They&apos;ll be added to the allowlist immediately and receive a branded email with the sign-in link.
          </p>
        </div>
        <div className="p-6 space-y-4">
          <label className="block">
            <span className="text-[11px] font-medium tracking-[0.16em] text-zinc-500 uppercase">Email address</span>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
              placeholder="an.other@ekselens.com"
              className="mt-2 w-full border border-zinc-300 px-3 py-2 text-[14px]"
              data-testid="invite-email-input"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium tracking-[0.16em] text-zinc-500 uppercase">Their name <span className="text-zinc-400 normal-case tracking-normal">(optional)</span></span>
            <input
              type="text"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              maxLength={120}
              className="mt-2 w-full border border-zinc-300 px-3 py-2 text-[14px]"
              data-testid="invite-name-input"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium tracking-[0.16em] text-zinc-500 uppercase">Personal note <span className="text-zinc-400 normal-case tracking-normal">(optional)</span></span>
            <textarea
              value={inviteNote}
              onChange={(e) => setInviteNote(e.target.value)}
              rows={3}
              maxLength={400}
              placeholder="e.g. Welcome to Ekselens — you'll be handling client engagements with me."
              className="mt-2 w-full border border-zinc-300 px-3 py-2 text-[14px]"
              data-testid="invite-note-input"
            />
          </label>
          <label className="block">
            <span className="text-[11px] font-medium tracking-[0.16em] text-zinc-500 uppercase">Role &amp; access level</span>
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              className="mt-2 w-full border border-zinc-300 px-3 py-2 text-[14px] bg-white"
              data-testid="invite-role-select"
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </label>
          {showTrialDays && (
            <label className="block" data-testid="invite-trial-days-wrap">
              <span className="text-[11px] font-medium tracking-[0.16em] text-zinc-500 uppercase">
                {inviteRole === "campaign_user" ? "Access window (working days, optional)" : "Trial length (working days)"}
              </span>
              <input
                type="number"
                min={1}
                max={60}
                value={inviteTrialDays}
                onChange={(e) => setInviteTrialDays(e.target.value)}
                className="mt-2 w-full border border-zinc-300 px-3 py-2 text-[14px]"
                data-testid="invite-trial-days-input"
              />
              <span className="text-[11px] text-zinc-400 mt-1 block">
                {inviteRole === "campaign_user"
                  ? "Counted in working days (weekends skipped). Leave blank for one-off access with no clock."
                  : "Counted in working days (weekends skipped). Access auto-expires after this. Default 5."}
              </span>
            </label>
          )}
        </div>
        <div className="px-6 py-4 bg-zinc-50 border-t border-zinc-200 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={inviting}
            className="text-[12px] tracking-[0.16em] uppercase text-zinc-600 hover:text-black"
            data-testid="invite-cancel"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={inviting || !inviteEmail.trim()}
            className="ek-btn ek-btn-primary text-[12.5px] disabled:opacity-50"
            data-testid="invite-submit"
          >
            <Send size={13} /> {inviting ? "Sending…" : "Send invitation"}
          </button>
        </div>
      </form>
    </div>
  );
}
